const WORKER_RESULT_LINE_PATTERN = /^\s*full result:\s*workers\//i;
const SCREENSHOT_SAVED_LINE_PATTERN =
  /^\s*screenshot saved in the result file\s*$/i;
const TRUNCATION_MARKER_PATTERN = /\(first\s+\d+\s+of\s+\d+\s+chars[^)]*\)/gi;
const WORKER_PATH_TOKEN_PATTERN = /workers\/[^\s)]+\.md/gi;
const CONTENT_HEADING_PATTERN = /^content\b[^:]*:\s*(.*)$/i;
const URL_LINE_PATTERN = /^url:\s*(.+)$/i;
const CONSOLE_HEADING_PATTERN = /^console:\s*$/i;
const CONSOLE_COUNT_PATTERN = /^console messages:\s*(\d+)\s*$/i;
const CONSOLE_TRUNCATED_PATTERN = /^console truncated:/i;
const CONSOLE_ENTRY_PATTERN = /^\[([A-Za-z]+)\]\s?([\s\S]*)$/;
const NETWORK_HEADING_PATTERN = /^network:\s*$/i;
const NETWORK_COUNT_PATTERN = /^network requests:\s*(\d+)\s*$/i;
const NETWORK_TRUNCATED_PATTERN = /^network truncated:/i;
const NETWORK_ENTRY_PATTERN =
  /^\s*-\s+id:\s+(\S+)\s+[—-]\s+([A-Za-z]+)\s+(\S+)(?:\s+→\s+(\d+))?\s*$/;
const VOID_HTML_TAGS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

export function cleanBrowserToolOutput(output: string): string {
  return output
    .split('\n')
    .filter(
      (line) =>
        !WORKER_RESULT_LINE_PATTERN.test(line) &&
        !SCREENSHOT_SAVED_LINE_PATTERN.test(line),
    )
    .join('\n')
    .replace(TRUNCATION_MARKER_PATTERN, '')
    .replace(WORKER_PATH_TOKEN_PATTERN, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export type BrowserConsoleLevel =
  | 'log'
  | 'info'
  | 'warning'
  | 'error'
  | 'debug';

export type BrowserConsoleEntry = {
  level: BrowserConsoleLevel;
  text: string;
};

const consoleLevel = (type: string): BrowserConsoleLevel => {
  const normalized = type.toLowerCase();

  if (
    normalized === 'error' ||
    normalized === 'pageerror' ||
    normalized === 'assert'
  ) {
    return 'error';
  }

  if (normalized === 'warning' || normalized === 'warn') {
    return 'warning';
  }

  if (normalized === 'info') {
    return 'info';
  }

  if (normalized === 'debug' || normalized === 'verbose') {
    return 'debug';
  }

  return 'log';
};

export function readBrowserConsole(output: string): {
  url?: string;
  messages: BrowserConsoleEntry[];
} | null {
  const lines = cleanBrowserToolOutput(output).split('\n');
  let url: string | undefined;
  let sawConsole = false;
  let countWithoutEntries = 0;
  const messages: BrowserConsoleEntry[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    const urlMatch = URL_LINE_PATTERN.exec(trimmed);

    if (urlMatch && !url) {
      url = urlMatch[1].trim();
      continue;
    }

    if (CONSOLE_HEADING_PATTERN.test(trimmed)) {
      sawConsole = true;
      continue;
    }

    const countMatch = CONSOLE_COUNT_PATTERN.exec(trimmed);

    if (countMatch) {
      sawConsole = true;
      countWithoutEntries = Number(countMatch[1]);
      continue;
    }

    if (CONSOLE_TRUNCATED_PATTERN.test(trimmed)) {
      continue;
    }

    const entryMatch = sawConsole ? CONSOLE_ENTRY_PATTERN.exec(line) : null;
    const entryType = entryMatch?.[1];

    if (entryMatch && entryType) {
      messages.push({
        level: consoleLevel(entryType),
        text: entryMatch[2],
      });
      continue;
    }

    const previous = messages.at(-1);

    if (previous) {
      previous.text = `${previous.text}\n${line}`;
    }
  }

  if (!sawConsole || (messages.length === 0 && countWithoutEntries > 0)) {
    return null;
  }

  return {
    ...(url ? { url } : {}),
    messages,
  };
}

export type BrowserNetworkRequest = {
  id: string;
  method: string;
  url: string;
  status?: number;
};

export function readBrowserNetwork(output: string): {
  url?: string;
  requests: BrowserNetworkRequest[];
} | null {
  const lines = cleanBrowserToolOutput(output).split('\n');
  let url: string | undefined;
  let sawNetwork = false;
  let countWithoutEntries = 0;
  const requests: BrowserNetworkRequest[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    const urlMatch = URL_LINE_PATTERN.exec(trimmed);

    if (urlMatch && !url && requests.length === 0) {
      url = urlMatch[1].trim();
      continue;
    }

    if (NETWORK_HEADING_PATTERN.test(trimmed)) {
      sawNetwork = true;
      continue;
    }

    const countMatch = NETWORK_COUNT_PATTERN.exec(trimmed);

    if (countMatch) {
      sawNetwork = true;
      countWithoutEntries = Number(countMatch[1]);
      continue;
    }

    if (NETWORK_TRUNCATED_PATTERN.test(trimmed)) {
      continue;
    }

    const entryMatch = NETWORK_ENTRY_PATTERN.exec(trimmed);

    if (!entryMatch) {
      continue;
    }

    sawNetwork = true;
    const status = entryMatch[4] ? Number(entryMatch[4]) : undefined;

    requests.push({
      id: entryMatch[1],
      method: entryMatch[2],
      url: entryMatch[3],
      ...(status && status > 0 ? { status } : {}),
    });
  }

  if (!sawNetwork || (requests.length === 0 && countWithoutEntries > 0)) {
    return null;
  }

  return {
    ...(url ? { url } : {}),
    requests,
  };
}

export function readBrowserResultUrl(output: string): string | undefined {
  const match = cleanBrowserToolOutput(output)
    .split('\n')
    .map((line) => URL_LINE_PATTERN.exec(line.trim()))
    .find((line) => line !== null);

  return match?.[1]?.trim() || undefined;
}

export function browserNavigateOutputWithoutUrlEcho({
  output,
  requestedUrl,
}: {
  output: string;
  requestedUrl?: string;
}): string {
  const requested = requestedUrl?.trim();

  return cleanBrowserToolOutput(output)
    .split('\n')
    .flatMap((line) => {
      const match = URL_LINE_PATTERN.exec(line.trim());

      if (!match) {
        return [line];
      }

      const landed = match[1].trim();

      if (requested && landed === requested) {
        return [];
      }

      return [landed];
    })
    .join('\n')
    .trim();
}

export function readBrowserPageSource(output: string): {
  url?: string;
  html: string;
} {
  const lines = cleanBrowserToolOutput(output).split('\n');
  let url: string | undefined;
  let htmlStart = -1;
  let inlineHtml = '';

  lines.forEach((line, index) => {
    if (htmlStart >= 0) {
      return;
    }

    const urlMatch = URL_LINE_PATTERN.exec(line.trim());

    if (urlMatch) {
      url = urlMatch[1].trim();

      return;
    }

    const contentMatch = CONTENT_HEADING_PATTERN.exec(line.trim());

    if (contentMatch) {
      htmlStart = index + 1;
      inlineHtml = contentMatch[1] ?? '';
    }
  });

  const html = [inlineHtml, ...(htmlStart >= 0 ? lines.slice(htmlStart) : [])]
    .filter((line) => line.trim().length > 0)
    .join('\n');

  return {
    ...(url ? { url } : {}),
    html: html.includes('<') ? html : '',
  };
}

export function formatHtmlSource(source: string): string {
  const lines = source
    .replace(/\r\n/g, '\n')
    .replace(/></g, '>\n<')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  let depth = 0;

  return lines
    .map((line) => {
      const tagName = /^<\/?([a-zA-Z0-9:-]+)/.exec(line)?.[1]?.toLowerCase();
      const isClosing = line.startsWith('</');
      const isVoid = tagName ? VOID_HTML_TAGS.has(tagName) : false;
      const isSelfClosing = /\/>$/.test(line) || isVoid;
      const isSpecial = line.startsWith('<!') || line.startsWith('<?');

      if (isClosing) {
        depth = Math.max(0, depth - 1);
      }

      const indented = `${'  '.repeat(depth)}${line}`;
      const opensAndCloses =
        tagName !== undefined &&
        new RegExp(
          `^<${tagName}\\b[^>]*>[\\s\\S]*</${tagName}>\\s*$`,
          'i',
        ).test(line);

      if (
        !isClosing &&
        !isSelfClosing &&
        !isSpecial &&
        !opensAndCloses &&
        tagName &&
        line.startsWith('<')
      ) {
        depth += 1;
      }

      return indented;
    })
    .join('\n');
}
